import type React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Switch, FormControlLabel, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { updateAdsPreference } from 'api/tenant'
import type { AppState } from 'state/index'
import colors from 'common/colors'

/**
 * Ad Preferences Settings Component
 * Allows premium subscribers to disable promotional ads
 */
export default function AdPreferences(): React.ReactElement | null {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const tenant = useSelector((state: AppState) => state.account.singleton)
  const currentSubscription = useSelector(
    (state: AppState) => state.account.currentSubscription,
  )

  // Only show for premium subscribers
  const isActivePremium =
    currentSubscription &&
    ['paid', 'comped', 'free_year'].includes(
      currentSubscription.subscriptionStatus,
    )

  if (!isActivePremium) return null

  const handleAdsDisabledChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateAdsPreference(e.target.checked) as any)
  }

  return (
    <Box
      sx={{
        mt: 3,
        p: 2,
        border: `1px solid ${colors.greyBorder}`,
        borderRadius: '4px',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t('ad_settings')}
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={tenant?.prefs?.ads?.disabled ?? false}
            onChange={handleAdsDisabledChange}
          />
        }
        label={t('ads_disabled_premium')}
      />
      <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
        {t('ads_disabled_premium_description')}
      </Typography>
    </Box>
  )
}
